import {
  BIOMES,
  WORLD_NODES,
  isSlotNodeType,
  isSlotUnlocked,
  type Biome,
  type WorldMapNode,
} from '@goodtime/shared';
// Stage 3 install-excision (2026-06-06) — locked icon now resolves
// via the asset manifest at render time (NOT module load — the
// AssetManifestClient's dev-fallback prefix is configured by
// {@code initManifestClient} which fires inside App's mount effect,
// after module load. Keeping the resolution lazy via getter so the
// first render after init reads the correct URL). play_button stays
// bundled — it's shared UI chrome under 100KB.
import { resolveEntryUrl } from '@goodtime/engine';
import playButtonPng from '../../../assets/ui_button/play.png?url';
import { Icon } from '../../Icon';
const lockedPng = (): string =>
  resolveEntryUrl({ path: 'map_node/locked.png' }) ?? '';

/**
 * T2 — All-Biomes List Screen.
 *
 * Replaces the per-biome travel portal as the canonical biome-navigation
 * surface. The producer brief calls for the player to land here at the
 * start AND end of each biome — pick which Episode to explore, see at a
 * glance how many slots remain locked in each biome, then enter.
 *
 * <p>Layout: vertical stack of 5 rows on phone, one per biome. Each row
 * shows:
 * <ul>
 *   <li>A circular thumbnail of the biome's commissioned panorama
 *       (reused from the bg + mg asset folders — no new commission).</li>
 *   <li>EPISODE N · biome name · "blurb" (matches the title-card chrome
 *       in {@link MapHud} so the visual language is consistent).</li>
 *   <li>A row of opaque-gem placeholders, one per locked slot in that
 *       biome. The first slot of each biome is free by default (the
 *       {@code FREE_SLOT_NODE_IDS} derivation in shared), so a fresh
 *       profile sees 6 placeholders in Kingdom (it has 7 slots total)
 *       and 1 in each of the other biomes.</li>
 *   <li>A gold play-circle on the right (chevron icon) that invites the
 *       tap — the whole row is the click target so a quick thumb-tap
 *       routes through; the chevron is the visual affordance.</li>
 * </ul>
 *
 * <p>Coupled to T1 (the opaque-gem placeholders are the visible surface
 * of the slot-unlock economy). When a slot unlocks, its placeholder
 * disappears here.
 */

const BG_MODULES = import.meta.glob<{ default: string }>(
  '../../../assets/map_bg/*.png',
  { eager: true, query: '?url' },
);
const MG_MODULES = import.meta.glob<{ default: string }>(
  '../../../assets/map_mg/*.png',
  { eager: true, query: '?url' },
);
// Dedicated, subject-forward biome thumbnails (the round image on each
// episode card). A square painting of the biome's landmark composed for
// the circular crop — replaces cropping the sky-heavy map_bg panorama.
// Optional per biome: a biome without one falls back to the map_bg +
// map_mg composite below, so the glob being empty is fine.
const HERO_MODULES = import.meta.glob<{ default: string }>(
  '../../../assets/biome_hero/*.png',
  { eager: true, query: '?url' },
);
// Per-slot map_node icons (themed_reels.png + the plain slot.png).
// Looked up by {@link WorldMapNode#type} so each pip shows the slot's
// actual art — Royal Reels gets the royal_reels coin, Dragon's Hoard
// the dragon one, etc.
//
// Stage 3 install-excision (2026-06-06) — used to use a wildcard
// glob that bundled every {@code map_node/*.png} (~64MB into the
// install). Now resolved via {@link resolveEntryUrl} at lookup time;
// the manifest tells us if the file exists, falling back to undefined
// when it doesn't.
import { AssetManifestClient } from '@goodtime/engine';
// Cinematic backdrop for the episodes list. Painterly twilight world
// vista that holds the center band clear for the overlay cards;
// commissioned via the toolgen pipeline (`generateImage` against
// {@code apps/mobile/src/assets/cinematic/episodes_bg.png}).
const EPISODES_BG_MODULES = import.meta.glob<{ default: string }>(
  '../../../assets/cinematic/episodes_bg.png',
  { eager: true, query: '?url' },
);
const EPISODES_BG_URL: string | undefined =
  Object.values(EPISODES_BG_MODULES)[0]?.default;

export function iconForNode(node: WorldMapNode): string | undefined {
  const path = `map_node/${node.type}.png`;
  const manifest = AssetManifestClient.shared.getManifest();
  if (manifest && !manifest.entries[path]) {
    return undefined;
  }
  return resolveEntryUrl({ path }) ?? undefined;
}

function bgFor(biomeId: Biome['id']): string | undefined {
  for (const [path, mod] of Object.entries(BG_MODULES)) {
    if (path.endsWith(`/${biomeId}.png`)) return mod.default;
  }
  return undefined;
}
function mgFor(biomeId: Biome['id']): string | undefined {
  for (const [path, mod] of Object.entries(MG_MODULES)) {
    if (path.endsWith(`/${biomeId}.png`)) return mod.default;
  }
  return undefined;
}
function heroFor(biomeId: Biome['id']): string | undefined {
  for (const [path, mod] of Object.entries(HERO_MODULES)) {
    if (path.endsWith(`/${biomeId}.png`)) return mod.default;
  }
  return undefined;
}

export interface BiomeListScreenProps {
  /** The player's purchased slot ids (stringified WorldMapNode.id).
   *  Used together with the shared {@code isSlotUnlocked} helper to
   *  count how many slots remain locked per biome. The first slot in
   *  each biome is free by default and isn't stored here. Optional —
   *  treated as empty when missing so the screen renders correctly
   *  even if the parent hasn't hydrated profile state yet (U12 M1). */
  unlockedSlotIds?: ReadonlyArray<string>;
  /** Slot node ids (stringified WorldMapNode.id) the player has
   *  favourited. Drives the favourites banner above the biome rows; the
   *  banner is hidden entirely until at least one favourite exists.
   *  Optional — treated as empty when missing. */
  favouriteSlotIds?: ReadonlyArray<string>;
  /** Launch a favourited slot from the banner. Receives the resolved
   *  {@link WorldMapNode}; the host routes it through the same
   *  unlock-checked play path the world map uses. Omit to make the
   *  banner tiles inert. */
  onPlayFavourite?: (node: WorldMapNode) => void;
  /** Fired when the player taps a biome row. The handler receives the
   *  BIOMES array index (0..4) so the parent can flip its
   *  {@code currentBiomeIdx} state and route to the WorldMapV2 view. */
  onSelectBiome: (biomeIdx: number) => void;
}

/** Stable empty-array reference used as the default for the optional
 *  {@code unlockedSlotIds} prop (U12 M1). Defined at module scope so
 *  every render with an absent prop receives the SAME array reference
 *  — protects any future {@code useMemo([unlockedSlotIds])} callers
 *  from a default-arg-creates-new-array gotcha. */
const EMPTY_UNLOCKS: ReadonlyArray<string> = [];

/** All slot-type nodes (e.g. 'slot', 'royal_reels', etc.) in a biome.
 *  Daily / chest / boss / locked nodes are filtered out — they don't
 *  participate in the unlock economy. */
function slotNodesInBiome(biomeIdx: number): readonly WorldMapNode[] {
  return WORLD_NODES.filter(
    (n) => n.biome === biomeIdx && isSlotNodeType(n.type),
  );
}

/** {@link WorldMapNode} pre-tagged with its current lock status —
 *  feed straight into the {@link SlotIconPip} renderer. */
interface SlotPipData {
  node: WorldMapNode;
  locked: boolean;
}

/** Every slot in a biome, paired with its current lock status. The
 *  free-by-default first slot reports {@code locked: false} so the
 *  row shows it as an available pip (slot icon without the padlock
 *  overlay) rather than hiding it. */
function slotPipsForBiome(
  biomeIdx: number,
  unlockedSlotIds: ReadonlyArray<string>,
): SlotPipData[] {
  return slotNodesInBiome(biomeIdx).map((node) => ({
    node,
    locked: !isSlotUnlocked(node.id, unlockedSlotIds),
  }));
}

export function BiomeListScreen({
  unlockedSlotIds = EMPTY_UNLOCKS,
  favouriteSlotIds = EMPTY_UNLOCKS,
  onPlayFavourite,
  onSelectBiome,
}: BiomeListScreenProps) {
  // Resolve favourited node ids → their WorldMapNode, dropping any that no
  // longer exist (e.g. a slot pulled from the registry) or aren't slots.
  const favouriteNodes = favouriteSlotIds
    .map((id) => WORLD_NODES.find((n) => String(n.id) === id))
    .filter((n): n is WorldMapNode => n != null && isSlotNodeType(n.type));

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        // Commissioned painterly backdrop when present (toolgen-built;
        // see {@link EPISODES_BG_URL} above); falls back to the original
        // navy gradient if the asset hasn't been generated yet.
        background: EPISODES_BG_URL
          ? `linear-gradient(180deg, rgba(15,27,60,0.55) 0%, rgba(27,44,86,0.65) 35%, rgba(43,65,120,0.78) 100%), url(${EPISODES_BG_URL}) center / cover no-repeat`
          : 'linear-gradient(180deg, #0F1B3C 0%, #1B2C56 35%, #2B4178 100%)',
        color: '#E9F0FF',
        fontFamily: '"Fredoka", -apple-system, system-ui, sans-serif',
        // Top padding clears the App-level wallet pill: that pill is
        // absolute-positioned at {@code top: 14 + safe-area-inset-top,
        // right: 18} with a ~38-44px chrome height depending on chip
        // mix (streak + level chips). On iOS Capacitor the safe-area
        // inset adds ~59px under the Dynamic Island; the
        // env(safe-area-inset-top) here mirrors that so the eyebrow
        // text doesn't run into the pill.
        // iOS pre-flight fix (2026-06-07).
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 96px)',
        // iOS pre-flight (2026-06-07) + scroll fix — extend the bottom
        // inset well past the home indicator AND the floating Friends /
        // Join-with-code pills (App-level, bottom-left, stacked up to
        // ~126px) so the last biome (Neon Heights) scrolls fully clear
        // of them. Was +32px → the last card hid behind the social pills.
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 150px)',
      }}
    >
      {/* Header */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: 24,
          padding: '0 24px',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            opacity: 0.6,
            textTransform: 'uppercase',
          }}
        >
          Choose Your Episode
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 800,
            marginTop: 4,
            textShadow: '0 2px 0 rgba(0,0,0,0.45)',
          }}
        >
          Goodtime
        </div>
        {/* Collection moved into the wallet HUD's 🏆 chip (2026-06-07);
            see BalancePill `collection` prop wired in App. */}
      </div>

      {/* Favourites banner (2026-06-20) — only rendered once the player
          has hearted at least one slot. Sits above the biome rows. */}
      {favouriteNodes.length > 0 ? (
        <FavouritesBanner nodes={favouriteNodes} onPlay={onPlayFavourite} />
      ) : null}

      {/* Biome rows — single column on phones, 2-column grid on wider
          screens. Producer brief 2026-06-04: on a desktop / tablet the
          single 540px-wide stack wastes the rest of the viewport. The
          {@code minmax(min(480px, 100%), 1fr)} rule keeps each row at
          480px on tablet/desktop (the tightest the title row's text +
          pips + play medallion read at), so it flips 1 → 2 columns only
          when the viewport fits ≥ 2 of those. The {@code min(480px,
          100%)} guard is what makes it phone-safe: on a viewport
          narrower than 480px (every iPhone — SE is 375pt) the track
          collapses to 100% of the container instead of forcing a 480px
          column that overflows the screen and clips the biome title +
          the rightmost slot pips (375pt fix 2026-06-07). max-width caps
          the layout at 2 columns so a 4K screen doesn't sprout 4 rows
          side by side. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(480px, 100%), 1fr))',
          gap: 14,
          padding: '0 16px',
          maxWidth: 1100,
          margin: '0 auto',
        }}
      >
        {BIOMES.map((biome, idx) => {
          const pips = slotPipsForBiome(idx, unlockedSlotIds);
          return (
            <BiomeRow
              key={biome.id}
              biome={biome}
              biomeIdx={idx}
              pips={pips}
              onSelect={() => onSelectBiome(idx)}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Favourites banner (2026-06-20) — a horizontal rail of the player's
 *  hearted slots, shown above the biome rows. The host only renders this
 *  when {@code nodes} is non-empty, so a player with no favourites never
 *  sees it. Each tile launches its slot through the same unlock-checked
 *  play path the world map uses. */
function FavouritesBanner({
  nodes,
  onPlay,
}: {
  nodes: readonly WorldMapNode[];
  onPlay?: (node: WorldMapNode) => void;
}) {
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto 18px', padding: '0 16px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
          paddingLeft: 2,
        }}
      >
        <Icon name="heart" size={16} alt="" />
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 2,
            textTransform: 'uppercase',
            opacity: 0.75,
          }}
        >
          Your favourites
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 10,
          overflowX: 'auto',
          paddingBottom: 4,
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {nodes.map((node) => (
          <FavouriteTile key={node.id} node={node} onPlay={onPlay} />
        ))}
      </div>
    </div>
  );
}

/** One favourite-slot tile — themed map-node icon + name, the whole tile
 *  taps to launch. The section's "Your favourites" heading already carries
 *  the heart, so the tiles themselves stay badge-free. */
function FavouriteTile({
  node,
  onPlay,
}: {
  node: WorldMapNode;
  onPlay?: (node: WorldMapNode) => void;
}) {
  const icon = iconForNode(node);
  return (
    <button
      type="button"
      onClick={() => onPlay?.(node)}
      aria-label={`Play ${node.name}`}
      style={{
        flex: '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        width: 88,
        padding: '10px 8px',
        background:
          'linear-gradient(135deg, rgba(255,228,138,0.14), rgba(255,255,255,0.06))',
        border: '1px solid rgba(255,228,138,0.35)',
        borderRadius: 14,
        cursor: 'pointer',
        color: 'inherit',
        font: 'inherit',
        boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
      }}
    >
      <span
        style={{
          position: 'relative',
          width: 52,
          height: 52,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon ? (
          <img
            src={icon}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 4px rgba(255,225,130,0.55))',
            }}
          />
        ) : null}
      </span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1.15,
          textAlign: 'center',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textShadow: '0 1px 0 rgba(0,0,0,0.5)',
        }}
      >
        {node.name}
      </span>
    </button>
  );
}

/** One row in the biome list. Whole row is the tap target. */
function BiomeRow({
  biome,
  biomeIdx,
  pips,
  onSelect,
}: {
  biome: Biome;
  biomeIdx: number;
  pips: SlotPipData[];
  onSelect: () => void;
}) {
  const hero = heroFor(biome.id);
  const bg = bgFor(biome.id);
  const mg = mgFor(biome.id);
  // Cap pip rendering at 12 — pathological biomes get a "+N more" tag
  // so the row never overflows. Below the cap, each pip is flex-sized
  // so 7 pips space out comfortably and 11 still fit without wrapping
  // (see {@link SlotIconPip}'s flex:1 1 0 + max-width:44 rule).
  const PIP_VISIBLE_CAP = 12;
  const visiblePips = pips.slice(0, PIP_VISIBLE_CAP);
  const overflowPips = Math.max(0, pips.length - PIP_VISIBLE_CAP);
  const lockedCount = pips.reduce((n, p) => (p.locked ? n + 1 : n), 0);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Enter ${biome.chapter} · ${biome.name}${
        lockedCount > 0 ? ` (${lockedCount} games locked)` : ' (all unlocked)'
      }`}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        background: `linear-gradient(135deg, ${biome.accent}38, ${biome.sky[2]}28)`,
        border: 'none',
        borderRadius: 18,
        cursor: 'pointer',
        color: 'inherit',
        textAlign: 'left',
        font: 'inherit',
        boxShadow:
          '0 6px 14px rgba(0,0,0,0.35), inset 0 1px 2px rgba(255,255,255,0.18)',
        // Subtle hover/active state via CSS — no React handler needed.
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
      }}
    >
      {/* Circular thumbnail of the biome panorama */}
      <div
        style={{
          flex: '0 0 auto',
          // Responsive thumbnail — shrinks on narrow phones so the title
          // + pips keep their room (was a flat 100px, which ate half of a
          // 375pt row's width). Caps at 100px on tablet/desktop. 375pt
          // fix 2026-06-07.
          width: 'clamp(64px, 22vw, 100px)',
          height: 'clamp(64px, 22vw, 100px)',
          borderRadius: '50%',
          padding: 3,
          background:
            'radial-gradient(circle at 30% 25%, #FFE9A8, #C9A24C 60%, #8A6824)',
          boxShadow:
            '0 0 12px 2px rgba(255,225,130,0.4), inset 0 -2px 4px rgba(80,55,15,0.55)',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            position: 'relative',
            background: `linear-gradient(180deg, ${biome.sky[0]}, ${biome.sky[1]} 60%, ${biome.sky[2]})`,
            boxShadow: 'inset 0 3px 6px rgba(0,0,0,0.4)',
          }}
        >
          {hero ? (
            // Dedicated subject-forward thumbnail — a complete square
            // painting, so it carries the whole circle on its own (no
            // bg/mg compositing). object-position center keeps the
            // landmark centred in the crop.
            <img
              src={hero}
              alt=""
              draggable={false}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          ) : (
            // Fallback for biomes without a hero yet — the old map_bg
            // (far sky) + map_mg (props) composite.
            <>
              {bg && (
                <img
                  src={bg}
                  alt=""
                  draggable={false}
                  // U12 L1 (2026-05-31) — on a 404 (asset still in
                  // pipeline / mid-migration), hide the failed layer.
                  // The radial-gradient backdrop underneath stays
                  // visible, so the thumbnail reads as muted-but-styled
                  // rather than a broken-image glyph.
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                />
              )}
              {mg && (
                <img
                  src={mg}
                  alt=""
                  draggable={false}
                  // U12 L1 (2026-05-31) — same fallback as bg above:
                  // hide the layer on 404 so the sky gradient + bg (if
                  // present) carry the thumbnail.
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'bottom',
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Title + placeholders block — vertical stack:
          (1) title row = episode label + name + blurb on left, play
              medallion right-aligned;
          (2) per-slot pips row spans the FULL middle-column width.
          Producer brief 2026-06-04: the play button used to live in
          its own column to the right of the whole text block, which
          stole vertical space from the pips. Moving it up onto the
          title row lets the pips spread out across the row width. */}
      <div
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            width: '100%',
          }}
        >
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            {/* Producer brief 2026-06-04 — "EPISODE N" eyebrow removed.
                There's no sequential concept (biomes unlock by gem
                progression, not 1-2-3-4), so the numbered framing was
                misleading. Biome name carries the row's identity on
                its own. */}
            <div
              style={{
                // Scales down on narrow phones; caps at 26px. Paired with
                // overflowWrap so a long biome name ("Greendale Kingdom")
                // wraps at the space instead of running off the right edge
                // — the original flat 26px clipped to "Greendale Kingdor"
                // on a 375pt SE. 375pt fix 2026-06-07.
                fontSize: 'clamp(20px, 5.4vw, 26px)',
                fontWeight: 800,
                lineHeight: 1.1,
                overflowWrap: 'break-word',
                textShadow: '0 1px 0 rgba(0,0,0,0.5)',
              }}
            >
              {biome.name}
            </div>
            <div
              style={{
                fontSize: 13,
                fontStyle: 'italic',
                opacity: 0.7,
                lineHeight: 1.3,
                marginTop: 4,
              }}
            >
              “{biome.blurb}”
            </div>
          </div>
          <img
            src={playButtonPng}
            alt=""
            aria-hidden
            style={{
              flex: '0 0 auto',
              // Shrinks with the rest of the row on narrow phones so it
              // doesn't crowd the title; caps at 56px. 375pt fix
              // 2026-06-07.
              width: 'clamp(44px, 13vw, 56px)',
              height: 'clamp(44px, 13vw, 56px)',
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))',
            }}
          />
        </div>

        {/* Per-slot pips — one per slot in this biome. The free-by-
            default first slot renders without a lock overlay so the
            player sees it as available; the rest render with the
            chained-padlock locked.png overlaid on the slot's own icon. */}
        {pips.length > 0 ? (
          <div
            data-biome-idx={biomeIdx}
            style={{
              display: 'flex',
              gap: 5,
              marginTop: 8,
              alignItems: 'center',
              flexWrap: 'nowrap',
              minWidth: 0,
              width: '100%',
              // Producer brief 2026-06-04 — distribute spare row width
              // into the gaps so the FIRST pip sticks to the left edge
              // and the LAST pip sticks to the right edge (aligning
              // with the play button on the title row above). With
              // {@link SlotIconPip}'s maxWidth:44, when the pip count
              // doesn't fill the row, this is what pushes the rightmost
              // medallion out to the right edge instead of leaving a
              // ragged gap.
              justifyContent: 'space-between',
            }}
          >
            {visiblePips.map((pip) => (
              <SlotIconPip key={pip.node.id} node={pip.node} locked={pip.locked} />
            ))}
            {overflowPips > 0 ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  opacity: 0.7,
                  marginLeft: 4,
                }}
              >
                +{overflowPips}
              </span>
            ) : null}
          </div>
        ) : null}
        {lockedCount === 0 && pips.length > 0 ? (
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              fontWeight: 700,
              color: '#7CFFAB',
              textShadow: '0 1px 0 rgba(0,0,0,0.55)',
            }}
          >
            <Icon name="check" size={12} alt="" /> All games unlocked
          </div>
        ) : null}
      </div>

      {/* Play button moved up onto the title row (see above) per
          producer brief 2026-06-04 — keeps the pips row full-width. */}
    </button>
  );
}

/** One slot pip — shows the slot's own {@code map_node/<type>.png}
 *  icon (the themed coin / map-node badge); locked slots get the
 *  chained-padlock {@code locked.png} overlay on top so the player
 *  sees "this themed slot exists here but you don't own it yet".
 *
 *  <p>No box / frame around the icon — the commissioned map-node art
 *  is already a finished medallion (gold rim + bevel + centre motif),
 *  so wrapping it in a UI tile flattens the visual. Locked pips just
 *  dim + desaturate the icon underneath the padlock overlay so the
 *  lock reads as the dominant element. */
function SlotIconPip({ node, locked }: { node: WorldMapNode; locked: boolean }) {
  const icon = iconForNode(node);
  // Locked pips render ONLY the chained-padlock locked.png — no themed
  // icon bleed-through underneath. Producer brief 2026-06-04: the
  // previous version dimmed + desaturated the slot's own icon and
  // overlaid the padlock on top, which gave every locked pip a faint
  // biome tint (forest green, neon purple, frost blue, etc.) — Jon
  // flagged the colour variance as inconsistent. Unlocked pips still
  // get their themed map-node coin so the player can see WHICH
  // themed slots exist in the biome.
  const src = locked ? lockedPng() : icon;
  return (
    <span
      aria-hidden
      title={node.name}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: 44,
        aspectRatio: '1 / 1',
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            // Subtle gold glow on available pips so the eye picks them
            // out from the locked-grey siblings.
            filter: locked
              ? undefined
              : 'drop-shadow(0 0 4px rgba(255,225,130,0.55))',
          }}
        />
      ) : null}
    </span>
  );
}
